'use client';
import React from 'react';

import { cn } from '@/lib/utils';

import { SvgIcon } from '@/components/SvgIcon';

import Button from '@/components/atoms/Button';

import { useAccordion } from './AccordionProvider';

import iconChevronDownSrc from '~/svg/IconChevronDown.svg';
export type AccordionItemProps = {
  children: React.ReactNode;
  header: React.ReactNode;
  id: string;
  classNames?: {
    wrapper?: string;
    headerWrapper?: string;
    icon?: string;
    contentWrapper?: string;
    content?: string;
  };
};

export default function AccordionItem({
  children,
  header,
  id,
  classNames,
}: AccordionItemProps) {
  const { getIsItemOpen, toggleItemId } = useAccordion();

  const isOpen = getIsItemOpen(id);

  function handleToggle() {
    toggleItemId(id);
  }

  function getValidChildren(node: React.ReactNode): React.ReactNode[] {
    return React.Children.toArray(node).filter((child) => {
      if (!child) return false;
      if (typeof child === 'string' && child.trim() === '') return false;
      if (React.isValidElement(child) && child.type === 'br') return false;
      return true;
    });
  }

  return (
    <div
      className={cn(
        'flex flex-col border-t border-solid border-gray-200 dark:border-gray-300',
        classNames?.wrapper
      )}
    >
      <div
        onClick={handleToggle}
        className={cn(
          'text-subtitle-l-d flex cursor-pointer items-center justify-between py-6 font-serif text-gray-950',
          classNames?.headerWrapper
        )}
        role='button'
        data-tracker-component={`accordion-item-unfold-${id}`}
      >
        {header}
        <Button noStyles>
          <SvgIcon
            src={iconChevronDownSrc}
            className={cn(
              'min-h-[1.5rem] min-w-[1.5rem] text-gray-950 transition-all',
              isOpen && 'rotate-180',
              classNames?.icon
            )}
            data-tracker-component={`accordion-item-unfold-chevron-${id}`}
          />
        </Button>
      </div>
      <div
        className={cn(
          isOpen ? 'grid-rows-[1fr] pb-6' : 'grid-rows-[0fr]',
          'grid transition-all duration-300 ease-out',
          classNames?.contentWrapper
        )}
      >
        <div className='overflow-hidden hover:overflow-visible'>
          <div
            className={cn(
              'relative text-gray-500 flex flex-col gap-y-8',
              classNames?.content
            )}
          >
            {getValidChildren(children)}
          </div>
        </div>
      </div>
    </div>
  );
}
